/**
 * @file CategoryBannerFields.tsx
 * @description Поля баннера шапки категории (phase19.16 Р2а; решение
 * пользователя 2026-10-12 «Да» — тот же выбор, что у обложки ленты блога
 * `BlogCoverFields`): «Как показать фото» схемами [Фоном][Справа][Слева]
 * (`categoryBanner.layout`), затемнение числом 0–60 % с ползунком (SB120,
 * `NumberSliderField`; `dim`, доля; ступени 0/20/40/60 — подсказки)
 * и тон текста (`textTone`) со справкой о контрасте (умолчание — затемнение 0,
 * как было; под текстом градиент, поэтому тон info). Точки фокуса здесь нет —
 * она своя у каждой категории; при «Баннере» под полями ссылка в её карточку.
 *
 * Правило «не прятать»: при шапке без баннера все поля на месте, но
 * неактивны (`fieldset disabled`) с пояснением; при фото сбоку так же
 * гаснут затемнение и тон.
 */

import { DEFAULT_SITE_CATALOG_OPTIONS } from "@chaiberi/shared";
import { Link } from "react-router";

import { FieldHint, GroupField } from "../../ui/settings-groups";
import { OptionCards } from "../OptionCards";
import { Segmented } from "../Segmented";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { CoverLayoutScheme } from "../schemes/CoverSchemes";
import {
  BANNER_LOW_DIM_NOTE,
  BANNER_OFF_NOTE,
  BANNER_SIDE_NOTE,
  CATEGORY_BANNER_LAYOUTS,
} from "./category-header-options";
import type { CatalogGroupProps } from "./CatalogListGroups";
import { COVER_TEXT_TONES, coverLowContrast } from "../../../lib/site-view/cover-layout";
import { ownValue } from "../../../lib/site-fields/number-input";
import { DIM_FIELD } from "../../../lib/site-view/page-bg";
import { useCurrentContext } from "../../../auth/hooks";
import { cn } from "@/lib/utils";

type Banner = CatalogGroupProps["options"]["categoryBanner"];

const LAYOUT_CARDS = CATEGORY_BANNER_LAYOUTS.map((l) => ({
  value: l.value,
  label: l.label,
  preview: <CoverLayoutScheme layout={l.value} />,
}));
/** Умолчание — 0 «как было» (ровного слоя нет, только градиент снизу); «↺» возвращает его. */
const BANNER_DIM_DEFAULT = DEFAULT_SITE_CATALOG_OPTIONS.categoryBanner.dim;

export function CategoryBannerFields({ options, onChange }: CatalogGroupProps) {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const banner = options.categoryBanner;
  const on = options.categoryHeader === "banner";
  const background = banner.layout === "background";
  // В форму — объект баннера целиком (сервер сливает по полям, но не гадаем).
  const set = (patch: Partial<Banner>) =>
    onChange({ ...options, categoryBanner: { ...banner, ...patch } });
  const risky = on && coverLowContrast(banner.layout, banner.textTone, banner.dim);
  return (
    <>
      {/* fieldset disabled гасит все кнопки внутри разом — поля видны, но не нажимаются. */}
      <fieldset
        disabled={!on}
        aria-describedby={on ? undefined : "category-banner-off-note"}
        className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0"
      >
        {on ? null : (
          <FieldHint id="category-banner-off-note" className="mt-0">
            {BANNER_OFF_NOTE}
          </FieldHint>
        )}
        <GroupField label="Как показать фото">
          {/* У карточек-схем своего вида «неактивно» нет — приглушаем обёрткой. */}
          <div className={cn("min-w-0", !on && "opacity-50")}>
            <OptionCards
              aria-label="Как показать фото баннера"
              value={banner.layout}
              onChange={(layout) => set({ layout })}
              options={LAYOUT_CARDS}
            />
          </div>
        </GroupField>
        {/* Вложенный fieldset: при фото сбоку текст не на фото — затемнение и тон не действуют. */}
        <fieldset
          disabled={!background}
          aria-describedby={on && !background ? "category-banner-side-note" : undefined}
          className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0"
        >
          {on && !background ? (
            <FieldHint id="category-banner-side-note" className="mt-0">
              {BANNER_SIDE_NOTE}
            </FieldHint>
          ) : null}
          <NumberSliderField
            label="Затемнение"
            {...DIM_FIELD}
            disabled={!on || !background}
            value={ownValue(banner.dim, BANNER_DIM_DEFAULT)}
            inherit={{ value: BANNER_DIM_DEFAULT, label: "без затемнения" }}
            onChange={(dim) => set({ dim: dim ?? BANNER_DIM_DEFAULT })}
          />
          <GroupField label="Текст на баннере">
            <Segmented
              aria-label="Текст на баннере"
              options={COVER_TEXT_TONES}
              value={banner.textTone}
              onChange={(textTone) => set({ textTone })}
            />
            <FieldHint className="mt-0">
              Тёмный — для светлых фото категорий; проверьте читаемость на канве.
            </FieldHint>
            {risky ? (
              <p className="type-caption rounded-md border-l-4 border-info bg-info/8 px-2 py-1 text-on-surface">
                {BANNER_LOW_DIM_NOTE}
              </p>
            ) : null}
          </GroupField>
        </fieldset>
      </fieldset>
      {/* Путь к фокусу — только при баннере и вне fieldset: ссылка не гаснет вместе с полями. */}
      {on ? (
        <FieldHint className="mt-0">
          Точка фокуса — у каждой категории своя:{" "}
          <Link
            to={`/shop/${slug}/catalog/categories`}
            className="inline-flex min-h-12 items-center font-medium text-primary hover:underline"
          >
            карточка категории → фото → фокус →
          </Link>
        </FieldHint>
      ) : null}
    </>
  );
}
